import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { CheckIcon } from "@heroicons/react/24/outline";

const PricingCard = ({ title, duration, price, features, planId }) => {
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentDetails, setPaymentDetails] = useState({
    email: localStorage.getItem("email") || "",
    phone: '',
    operator: 'mtn',
  });
  const [paymentStatus, setPaymentStatus] = useState('idle');
  const navigate = useNavigate();
  const isAuthenticated = localStorage.getItem('token') !== null;

  const generateReference = () => {
    const timestamp = Date.now();
    const randomNum = Math.floor(Math.random() * 1000);
    return `desac-${timestamp}-${randomNum}`;
  };

  const handleClick = () => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    setShowPaymentModal(true);
  };

  const handlePaymentSubmit = async (e) => {
    e.preventDefault();
    setPaymentStatus('processing');

    try {
      const referenceId = generateReference();
      
      // 1. Create subscription record in database
      const subscriptionResponse = await axios.post(
        `${process.env.REACT_APP_API_URL}/subscriptions/`,
        {
          planId: planId,
          referenceId,
          userId: localStorage.getItem('userId'),
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        }
      );

      // 2. Initiate payment with Lenco
      const paymentResponse = await axios.post(
        "https://payment-lenco-api.onrender.com/api/payment/request",
        {
          amount: parseFloat(price.replace(/[^0-9.]/g, '')),
          reference: referenceId,
          phone: paymentDetails.phone,
          operator: paymentDetails.operator,
          country: 'zm',
          bearer: 'customer'
        }
      );

      // 3. Start polling for payment verification
      const verifyPayment = async () => {
        try {
          const verifyResponse = await axios.get(
            `https://payment-lenco-api.onrender.com/api/payment/verify/${referenceId}`
          );
          
          if (verifyResponse.data.status === true && verifyResponse.data.data.status === 'successful') {
            // Format dates for the update request
            const startDate = new Date();
            const endDate = new Date();
            
            // Calculate end date based on plan duration
            if (duration === 'Daily') {
              endDate.setDate(endDate.getDate() + 1);
            } else if (duration === 'Weekly') {
              endDate.setDate(endDate.getDate() + 7);
            } else if (duration === 'Monthly') {
              endDate.setMonth(endDate.getMonth() + 1);
            } else if (duration === '3 Months') {
              endDate.setMonth(endDate.getMonth() + 3);
            }
            
            // Format dates as YYYY-MM-DD
            const formattedStartDate = startDate.toISOString().split('T')[0];
            const formattedEndDate = endDate.toISOString().split('T')[0];
            
            // Update subscription status
            await axios.put(
              `${process.env.REACT_APP_API_URL}/subscriptions/${referenceId}`,
              {
                startDate: formattedStartDate,
                endDate: formattedEndDate,
                active: true
              },
              {
                headers: {
                  Authorization: `Bearer ${localStorage.getItem('token')}`,
                },
              }
            );

            setPaymentStatus('success');
            setTimeout(() => {
              setShowPaymentModal(false);
              navigate('/');
            }, 2000);
          } else {
            // Continue polling if payment is still pending
            setTimeout(verifyPayment, 5000);
          }
        } catch (error) {
          console.error('Error verifying payment:', error);
          setPaymentStatus('error');
        }
      };

      // Start verification polling
      verifyPayment();

    } catch (error) {
      console.error('Error processing payment:', error);
      setPaymentStatus('error');
    }
  };

  return (
    <>
      <article className="flex w-full flex-col rounded-xl border border-line bg-surface-850 p-5 transition-all duration-200 hover:-translate-y-0.5 hover:border-line-strong sm:p-6">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="text-lg font-semibold tracking-tight text-white">{title}</h3>
            <p className="mt-1 text-sm text-gray-400">{duration}</p>
          </div>
          <span className="rounded-lg border border-accent/20 bg-accent/10 px-2.5 py-1 text-xs font-semibold text-accent-soft">PLAN</span>
        </div>
        <ul className="mb-6 mt-6 flex-1 space-y-3 text-sm text-gray-300">
          {features.map((feature, index) => (
            <li key={index} className="flex items-start gap-2.5">
              <CheckIcon aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0 text-accent-soft" />
              <span>{feature}</span>
            </li>
          ))}
        </ul>
        <button
          onClick={handleClick}
          className="btn-primary min-h-11 w-full"
        >
          Continue · K{price}
        </button>
      </article>

      {/* Payment Modal */}
      {showPaymentModal && (
        <div className="fixed inset-0 z-[1100] flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm">
          <div className="w-full max-w-md rounded-xl border border-line-strong bg-surface-850 p-5 shadow-2xl sm:p-6">
            <h2 className="text-2xl font-bold text-white mb-4">Complete Your Subscription</h2>
            <form onSubmit={handlePaymentSubmit}>
              <div className="mb-4">
                <label className="block text-gray-300 mb-2">Email</label>
                <input
                  type="email"
                  required
                  className="field"
                  value={paymentDetails.email}
                  onChange={(e) =>
                    setPaymentDetails({
                      ...paymentDetails,
                      email: e.target.value,
                    })
                  }
                  readOnly // Make it read-only since it's from localStorage
                />
                <p className="text-gray-400 text-sm mt-1">
                  Email is automatically filled from your account
                </p>
              </div>
              <div className="mb-4">
                <label className="block text-gray-300 mb-2">Phone Number</label>
                <input
                  type="tel"
                  required
                  className="field"
                  value={paymentDetails.phone}
                  onChange={(e) => setPaymentDetails({...paymentDetails, phone: e.target.value})}
                />
              </div>
              <div className="mb-6">
                <label className="block text-gray-300 mb-2">Payment Method</label>
                <select
                  className="field"
                  value={paymentDetails.operator}
                  onChange={(e) => setPaymentDetails({...paymentDetails, operator: e.target.value})}
                >
                  <option value="mtn">MTN Mobile Money</option>
                  <option value="airtel">Airtel Money</option>
                </select>
              </div>
              <div className="flex justify-end space-x-4">
                <button
                  type="button"
                  onClick={() => setShowPaymentModal(false)}
                  className="px-4 py-2 text-gray-300 hover:text-white"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={paymentStatus === 'processing'}
                  className="btn-primary"
                >
                  {paymentStatus === 'processing' ? 'Processing...' : 'Pay Now'}
                </button>
              </div>
              {paymentStatus === 'error' && (
                <p className="text-red-400 mt-4">Payment failed. Please try again.</p>
              )}
              {paymentStatus === 'success' && (
                <p className="text-green-400 mt-4">Payment successful! Redirecting...</p>
              )}
            </form>
          </div>
        </div>
      )}
    </>
  );
};

const PricingSection = ({ onLoadingChange }) => {
  const [pricingData, setPricingData] = useState([]);
  
  useEffect(() => {
    let isMounted = true;
    let retryTimeout;

    const fetchPricingPlans = async (retryCount = 0) => {
      if (!isMounted) return;

      try {
        const response = await fetch(process.env.REACT_APP_API_URL + '/subscription-plans');
        
        if (!response.ok) {
          throw new Error('Failed to fetch pricing plans');
        }
        
        const data = await response.json();
        if (isMounted) {
          const transformedData = data.map(plan => ({
            planId: plan.planId,
            title: plan.planName.toUpperCase() + " PLAN",
            duration: plan.duration,
            price: plan.price.replace("K", ""),
            features: plan.features.map(f => f.feature)
          }));
          
          setPricingData(transformedData);
        }
      } catch (error) {
        console.error("Error fetching pricing plans:", error);
        
        if (retryCount < 3 && isMounted) {
          console.log(`Retrying... Attempt ${retryCount + 1}`);
          retryTimeout = setTimeout(() => fetchPricingPlans(retryCount + 1), 2000);
          return;
        }
        
        if (isMounted) {
          setPricingData([
            {
              planId: "1",
              title: "BASIC PLAN",
              duration: "1 Month",
              price: "2000",
              features: ["Feature 1", "Feature 2", "Feature 3"]
            },
            {
              planId: "2",
              title: "PREMIUM PLAN",
              duration: "3 Months",
              price: "5000",
              features: ["Feature 1", "Feature 2", "Feature 3", "Feature 4"]
            }
          ]);
        }
      }
    };

    fetchPricingPlans();
    
    return () => {
      isMounted = false;
      if (retryTimeout) {
        clearTimeout(retryTimeout);
      }
    };
  }, []);

  return (
    <section className="relative bg-surface-950 py-10 sm:py-12">
      {/* Content Container */}
      <div className="relative z-10 mx-auto max-w-7xl px-5 sm:px-8 lg:px-10">
        <div className="mb-5">
          <p className="eyebrow-accent">MORE FROM DESAC</p>
          <h2 className="mt-1 text-2xl font-bold tracking-tight text-white sm:text-3xl">Plans & features</h2>
          <p className="mt-1 text-sm text-gray-400">Choose a plan that fits the way you enjoy DESAC TV.</p>
        </div>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          {pricingData.map((plan, index) => (
            <PricingCard key={index} {...plan} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default PricingSection;
